import React, { useState, useEffect, useRef } from 'react';
import {
  classifyTrainType, trainTypeIcons, trainTypeColors, categoryLabels, attributeLabels,
  brandColorFor, readableTextOn,
} from './trainUtils';
import { trainGroupLabels, DELAY_THRESHOLD_MIN, getDelay, isDelayed, delayColor } from './trainPosition';
import BaureiheBadge from './BaureiheBadge';
import imageCredits from '../data/trainImageCredits.json';
import { fetchTrainHistory } from './dashboardApi';

const clock = (iso) => (iso ? String(iso).slice(11, 16) : '—');

/**
 * The photo of the class, as the panel's header.
 *
 * It carries more at a glance than any of the text under it — a double-deck regional train
 * and an ICE 4 are told apart in the time it takes to look, and no label does that.
 *
 * The picture is keyed on the series UUID. It comes from the journey when there is one, and
 * from the map answer otherwise; without that fallback the majority of trains would have no
 * picture, because most runs have no journey.
 */
const TrainHero = ({ serie, color }) => {
  const [failed, setFailed] = useState(false);
  const uuid = serie?.trainSerieUUID;
  // CC BY and CC BY-SA both require the photographer to be named wherever the picture is
  // shown. Anything without an entry here is a photo that came with the project and needs
  // no credit line.
  const credit = imageCredits[uuid];
  const label = serie?.trainSerieName || serie?.trainSerieNumber;
  useEffect(() => { setFailed(false); }, [uuid]);
  if (!uuid || failed) return null;

  return (
    <figure className="tp-hero" style={{ '--tp-hero-accent': color }}>
      <img src={`/trains/${uuid}.png`} alt={label || 'Baureihe'} onError={() => setFailed(true)} />
      {(label || credit) && (
        <figcaption className="tp-hero-caption">
          {label && <b>{label}</b>}
          {serie.trainSerieOperator && <span>{serie.trainSerieOperator}</span>}
          {credit && (
            <a className="tp-hero-credit" href={credit.source}
               target="_blank" rel="noreferrer noopener"
               title={`${credit.author} — ${credit.licence}`}>
              Foto: {credit.author} · {credit.licence}
            </a>
          )}
        </figcaption>
      )}
    </figure>
  );
};

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * How this train has been running over the past months.
 *
 * One figure per operating day: the delay at the last stop recorded for the run. Days on
 * which nothing was measured are absent rather than zero - an unmeasured day is not a
 * punctual one, and drawing it as a flat bar would say it was.
 */
const HistoryBlock = ({ history, loading, failed }) => {
  if (loading) {
    return <div className="kbs-drillin-loading"><i className="fa fa-spinner fa-spin"></i> Historie wird geladen…</div>;
  }
  if (failed || !history || !history.days?.length) {
    return <p className="tp-offmap-note">Für diesen Zug liegen keine vergangenen Betriebstage vor.</p>;
  }

  // Fourteen columns fit a phone with the figure still legible above each one. Twenty-one
  // did not, and the height on its own said nothing - the scale named a single value.
  const days = history.days.slice(-14);
  const worst = Math.max(10, ...days.map(d => d.delay_min));
  const fmt = (v) => (v === null || v === undefined ? '–' : `${v > 0 ? '+' : ''}${v} min`);
  const pct = (v) => (v === null || v === undefined ? '–' : `${v} %`);

  return (
    <>
      <div className="kbs-drillin-section">
        <h5>Letzte Betriebstage</h5>
        <div className="tp-hist-bars">
          {days.map(d => (
            <div key={d.date} className="tp-hist-col" title={`${d.date}: ${fmt(d.delay_min)}`}>
              <span className="tp-hist-value" style={{ color: delayColor(d.delay_min) }}>
                {d.delay_min}
              </span>
              <span className="tp-hist-bar">
                <span
                  style={{
                    height: `${Math.max(2, Math.round(100 * Math.max(0, d.delay_min) / worst))}%`,
                    background: delayColor(d.delay_min),
                  }}
                />
              </span>
              <span className="tp-hist-day-label">{d.date.slice(8, 10)}.</span>
            </div>
          ))}
        </div>
        <p className="tp-hist-note">
          Verspätung in Minuten am letzten Halt · {days[0]?.date.slice(8, 10)}.{days[0]?.date.slice(5, 7)}.
          {' bis '}{days[days.length - 1]?.date.slice(8, 10)}.{days[days.length - 1]?.date.slice(5, 7)}.
        </p>
      </div>

      <div className="kbs-drillin-section">
        <h5>Durchschnitt</h5>
        <ul className="train-facts">
          {[['Letzte 14 Tage', history.recent], ['30 Tage', history.month],
            ['Gesamter Zeitraum', history.year]].map(([label, s]) => (
            <li key={label}>
              <span>{label}</span>
              <b>
                {fmt(s?.delay_avg_min)}
                <em className="tp-fact-age">{pct(s?.on_time_pct)} pünktlich · {s?.runs || 0} Tage</em>
              </b>
            </li>
          ))}
        </ul>
      </div>

      <div className="kbs-drillin-section">
        <h5>Nach Wochentag</h5>
        <div className="tp-hist-week">
          {history.weekday.map(w => (
            <div key={w.weekday} className="tp-hist-day">
              <span className="tp-hist-day-name">{WEEKDAYS[w.weekday]}</span>
              <span className="tp-hist-day-value" style={{ color: delayColor(w.delay_avg_min) }}>
                {w.delay_avg_min === null ? '–' : `${w.delay_avg_min > 0 ? '+' : ''}${w.delay_avg_min}`}
              </span>
              <span className="tp-hist-day-runs">{w.runs}×</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const segmentProgress = (train, nowMs) => {
  const a = train.prev_station;
  const b = train.next_station;
  if (!a?.time || !b?.time) return null;
  const t0 = Date.parse(a.time);
  const t1 = Date.parse(b.time);
  if (!isFinite(t0) || !isFinite(t1) || t1 <= t0) return null;
  return Math.max(0, Math.min(1, (nowMs - t0) / (t1 - t0)));
};

/** Scheduled time, actual time and delay for one endpoint — mirrors the detail header */
const EndpointTime = ({ station, field, label }) => {
  const scheduled = station?.[`${field}_scheduled`];
  const actual = station?.[`${field}_actual`];
  if (!scheduled) return null;
  const delay = getDelay(scheduled, actual);
  const late = isDelayed(scheduled, actual);
  return (
    <span className="tp-endpoint-time">
      <span className="tp-endpoint-label">{label}</span>
      <span className={delay ? 'tp-struck' : ''}>{clock(scheduled)}</span>
      {actual && delay ? (
        <span className={late ? 'tp-late' : 'tp-ontime'}>{clock(actual)} +{delay}</span>
      ) : null}
    </span>
  );
};

/** "14:32" — when the import last wrote this run */
const formatClock = (when) => {
  const d = when instanceof Date ? when : new Date(when);
  return isNaN(d) ? '—' : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

/** How long ago that was, in the words someone would use to judge whether it still holds */
const describeMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return '';
  // The map can stand at a past moment while the data was written later. "vor -300 Minuten"
  // is not an answer; saying the data came after the moment shown is.
  if (minutes < 0) return 'nach dem gezeigten Zeitpunkt erfasst';
  if (minutes < 1) return 'gerade eben';
  if (minutes === 1) return 'vor 1 Minute';
  if (minutes < 60) return `vor ${minutes} Minuten`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? 'vor 1 Stunde' : `vor ${hours} Stunden`;
};

/** Same, against the moment the map is showing rather than the wall clock */
const describeAge = (when, nowMs) => {
  const d = when instanceof Date ? when : new Date(when);
  if (isNaN(d)) return '';
  return describeMinutes(Math.floor((nowMs - d.getTime()) / 60000));
};

/**
 * Train detail inside the map's right-hand panel — the same slot the KBS and station
 * drill-ins use. Carries what the standalone detail view shows apart from its map, since
 * the run is already drawn large on the live map: series, endpoints, delay, route and
 * on-board features.
 */
const TrainPanel = ({
  train, journey, journeyLoading, journeyFailed = false,
  opDay, at, showFullDetailLink = false,
  onOpenDetail, onSelectKbs, onClose,
  onMap = false, onLocate, following = false, onToggleFollow,
}) => {
  // Everything with a time in it is read against the moment the map is showing. In the
  // point-in-time mode that is not now, and mixing the two puts a section from 20:00 next to
  // a label saying "50 minutes ago" measured from one in the morning.
  const nowMs = (at instanceof Date ? at.getTime() : Date.now());
  const [tab, setTab] = useState('overview');
  const [history, setHistory] = useState(null);
  const [historyState, setHistoryState] = useState('idle');
  const line = train?.trainLineName || train?.trainName;
  // Keyed on the run, not the line: the API anchors on where this run ends, so two S1 in
  // opposite directions have different histories
  const historyKey = train?.trainUUID || line;
  // Which train the history was asked for. A ref, not state: the effect used to list its own
  // state as a dependency, so setting it to "loading" ran the cleanup and every answer was
  // thrown away — the tab stayed on "wird geladen" however fast the backend replied.
  const historyFor = useRef(null);

  // Fetched when the tab is first opened, not when the panel is: it is a question about
  // months of data and most panel opens never ask it.
  useEffect(() => {
    if (tab !== 'history' || !historyKey || historyFor.current === historyKey) return;
    const key = historyKey;
    historyFor.current = key;
    setHistory(null);
    setHistoryState('loading');
    // An answer counts only while it is still for the train on show
    fetchTrainHistory(train)
      .then(h => {
        if (historyFor.current !== key) return;
        setHistory(h);
        setHistoryState('done');
      })
      .catch(() => {
        if (historyFor.current !== key) return;
        setHistoryState('failed');
        // Opening the tab again asks once more
        historyFor.current = null;
      });
    // train is read for the request only; the key is what decides whether to ask
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, historyKey]);

  if (!train) return null;

  const type = classifyTrainType(train);
  // Who runs this leg, from the journey's administration code where it resolves. The class
  // operator stays the fallback — and stays in the Baureihe block below, where it belongs.
  const operator = train.operator || train.trainSerieOperator;
  const color = brandColorFor(operator)
    || trainTypeColors[type] || trainTypeColors.default;
  const cancelled = train.status === 'cancelled';
  // The train was picked from a board or the search but is not under way at this moment
  const offMap = train.status === 'offmap' || train.lat === null;
  const delayed = train.current_delay >= DELAY_THRESHOLD_MIN;
  const progress = cancelled || offMap ? null : segmentProgress(train, nowMs);
  const showLine = train.trainLineName &&
    train.trainLineName.toLowerCase() !== (train.trainName || '').toLowerCase();

  const stations = journey?.stations || [];
  const first = stations[0];
  const last = stations[stations.length - 1];
  // Everything up to and including this stop has been served — the timeline is drawn solid
  // there and hollow beyond, so the current position reads at a glance
  const lastPassedIdx = train.prev_station
    ? stations.findIndex(s => s.stationName === train.prev_station.name)
    : -1;
  const attributes = journey?.attributes || [];
  const messages = journey?.messages || [];
  const series = journey?.trainSeries || [];
  // The journey knows the exact set; the map answer knows one, and knows it for every train
  const heroSerie = series[0] || (train.trainSerieUUID ? {
    trainSerieUUID: train.trainSerieUUID,
    trainSerieName: train.trainSerieName,
    trainSerieNumber: train.trainSerieNumber,
    trainSerieOperator: train.trainSerieOperator,
  } : null);
  const hasCapacity = stations.some(s => s.capacityLevelClass1 || s.capacityLevelClass2);

  const tabs = [
    { id: 'overview', icon: 'fa-info-circle', label: 'Übersicht' },
    stations.length > 0 && { id: 'route', icon: 'fa-road', label: 'Verlauf' },
    attributes.length > 0 && { id: 'attributes', icon: 'fa-tags', label: 'Ausstattung' },
    messages.length > 0 && { id: 'messages', icon: 'fa-exclamation-triangle', label: 'Meldungen' },
    line && { id: 'history', icon: 'fa-line-chart', label: 'Historie' },
  ].filter(Boolean);

  const activeTab = tabs.some(t => t.id === tab) ? tab : 'overview';

  return (
    <aside className="kbs-drillin train-panel">
      <div className="kbs-drillin-header">
        <button className="kbs-drillin-close" onClick={onClose} title="Schließen">
          <i className="fa fa-arrow-left"></i>
        </button>
        <div className="kbs-drillin-headtext">
          <div className="train-panel-title">
            <span className="train-panel-icon">{trainTypeIcons[type] || trainTypeIcons.default}</span>
            {showLine && (
              <span className="train-panel-line" style={{ background: color, color: readableTextOn(color) }}>
                {train.trainLineName}
              </span>
            )}
            <span className="train-panel-name">{train.trainName}</span>
            {cancelled && <span className="tp-cancelled-badge">Fällt aus</span>}
            {!cancelled && offMap && <span className="tp-offmap-badge">nicht unterwegs</span>}
          </div>
          <div className="kbs-drillin-date">
            {categoryLabels[train.trainTypeProductCategorie] || trainGroupLabels[train.train_group]}
            {operator && ` · ${operator}`}
            {!operator && train.operator_country && ` · ${train.operator_country}`}
            {train.trainNumber && ` · Zugnr. ${train.trainNumber}`}
          </div>
        </div>
      </div>

      {/* Both act on where the train is right now, so both need it on the map at this moment.
          Disabled rather than hidden: a missing button does not say why it is missing. */}
      {onLocate && (
        <div className="tp-map-actions">
          <button
            className="tp-map-btn"
            onClick={onLocate}
            disabled={!onMap}
            title={onMap
              ? 'Karte auf den Zug zentrieren'
              : 'Der Zug ist zu diesem Zeitpunkt nicht auf der Karte'}
          >
            <i className="fa fa-crosshairs"></i> Zum Zug
          </button>
          <button
            className={`tp-map-btn${following ? ' active' : ''}`}
            onClick={onToggleFollow}
            disabled={!onMap && !following}
            aria-pressed={following}
            title={following
              ? 'Folgen beenden — oder einfach die Karte verschieben'
              : onMap
                ? 'Die Karte wandert mit dem Zug mit'
                : 'Der Zug ist zu diesem Zeitpunkt nicht auf der Karte'}
          >
            <i className="fa fa-location-arrow"></i>
            {following ? ' Folgt' : ' Folgen'}
          </button>
        </div>
      )}

      <TrainHero serie={heroSerie} color={color} />

      {/* Start and end of the run. Where a journey was fetched its stops carry real
          times and platforms; where none was, the run still names both ends in its own
          journeyID and the API passes them through as `run`. Showing nothing there was the
          worse answer, and it was the usual one - most runs have no journey. */}
      {!(first && last) && (train.run?.origin?.name || train.run?.destination?.name) && (
        <div className="tp-endpoints">
          <div className="tp-endpoint">
            <div className="tp-endpoint-name">
              <i className="fa fa-play"></i> {train.run.origin?.name || '—'}
            </div>
            {train.run.origin?.time && (
              <span className="tp-endpoint-time">
                <span className="tp-endpoint-label">ab</span>
                <span>{clock(train.run.origin.time)}</span>
              </span>
            )}
          </div>
          <i className="fa fa-long-arrow-right tp-endpoint-arrow"></i>
          <div className="tp-endpoint tp-endpoint-right">
            <div className="tp-endpoint-name">
              <i className="fa fa-flag-checkered"></i> {train.run.destination?.name || '—'}
            </div>
            {train.run.destination?.time && (
              <span className="tp-endpoint-time">
                <span className="tp-endpoint-label">an</span>
                <span>{clock(train.run.destination.time)}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {first && last && (
        <div className="tp-endpoints">
          <div className="tp-endpoint">
            <div className="tp-endpoint-name"><i className="fa fa-play"></i> {first.stationName}</div>
            <EndpointTime station={first} field="departureTime" label="ab" />
            {first.track_scheduled && <span className="tp-track">Gl. {first.track_actual || first.track_scheduled}</span>}
          </div>
          <i className="fa fa-long-arrow-right tp-endpoint-arrow"></i>
          <div className="tp-endpoint tp-endpoint-right">
            <div className="tp-endpoint-name"><i className="fa fa-flag-checkered"></i> {last.stationName}</div>
            <EndpointTime station={last} field="arrivalTime" label="an" />
            {last.track_scheduled && <span className="tp-track">Gl. {last.track_actual || last.track_scheduled}</span>}
          </div>
        </div>
      )}

      <div className="tp-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`tp-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <i className={`fa ${t.icon}`}></i> {t.label}
          </button>
        ))}
      </div>

      {journeyLoading && !journey && (
        <div className="kbs-drillin-loading"><i className="fa fa-spinner fa-spin"></i> Fahrt wird geladen…</div>
      )}

      {journeyFailed && !journey && (
        <p className="tp-offmap-note">
          <i className="fa fa-exclamation-triangle"></i>{' '}
          Zu diesem Zug liegt kein Fahrtverlauf vor. Die Angaben oben stammen aus der Kartenabfrage.
        </p>
      )}

      {/* When the import last wrote this run — not when we fetched it. Our own request is
          always "just now" and says nothing; the age of the data is what tells the reader how
          much of the times below still holds. */}
      {journey?.collectedAt && (
        <p className="tp-fetched-at">
          <i className="fa fa-clock-o"></i>{' '}
          Fahrtdaten von {formatClock(journey.collectedAt)}
          <span>{describeAge(journey.collectedAt, nowMs)}</span>
        </p>
      )}

      {activeTab === 'overview' && (
        <>
          {(cancelled || offMap) && (
            <p className="tp-offmap-note">
              <i className="fa fa-info-circle"></i>{' '}
              {cancelled
                ? 'Dieser Zug ist ausgefallen und fährt daher nicht auf der Karte.'
                : 'Zu diesem Zeitpunkt ist der Zug nicht unterwegs — Fahrtverlauf und Ausstattung stehen trotzdem bereit.'}
            </p>
          )}

          <div className="kbs-drillin-kpis train-panel-kpis">
            <div className="kbs-kpi">
              <span
                className="kbs-kpi-value"
                style={{ color: cancelled ? 'var(--primary-color)' : delayed ? '#f78166' : '#3fb950' }}
              >
                {cancelled ? 'Ausfall' : offMap ? '—' : train.current_delay > 0 ? `+${train.current_delay}` : 'pünktlich'}
              </span>
              <span className="kbs-kpi-label">{cancelled ? 'Status' : 'Verspätung'}</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{clock(train.next_station?.time)}</span>
              <span className="kbs-kpi-label">nächster Halt</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">
                {cancelled ? '—' : offMap ? 'ruht' : train.status === 'at_station' ? 'im Bhf' : 'fährt'}
              </span>
              <span className="kbs-kpi-label">Lage</span>
            </div>
            <div className="kbs-kpi">
              <span className="kbs-kpi-value">{train.kbs_number || '—'}</span>
              <span className="kbs-kpi-label">KBS</span>
            </div>
          </div>

          {progress !== null && train.prev_station && train.next_station && (
            <div className="kbs-drillin-section">
              <h5>Aktueller Abschnitt</h5>
              <div className="train-progress">
                <div className="train-progress-track">
                  <div className="train-progress-fill" style={{ width: `${progress * 100}%`, background: color }} />
                  <div className="train-progress-dot" style={{ left: `${progress * 100}%`, borderColor: color }} />
                </div>
                <div className="train-progress-ends">
                  <span>
                    <b>{train.prev_station.name}</b>
                    <em>ab {clock(train.prev_station.time)}</em>
                  </span>
                  <span className="right">
                    <b>{train.next_station.name}</b>
                    <em>an {clock(train.next_station.time)}</em>
                  </span>
                </div>
              </div>
            </div>
          )}

          {series.length > 0 && (
            <div className="kbs-drillin-section">
              <h5>Baureihe</h5>
              {series.map((serie, i) => (
                <div key={serie.trainSerieUUID || i} className="tp-serie">
                  {/* The class already fills the header; a thumbnail of the same photo
                      underneath it says nothing the reader has not just seen. */}
                  {serie.trainSerieUUID !== heroSerie?.trainSerieUUID && (
                    <BaureiheBadge serie={serie} size="md" />
                  )}
                  <div className="tp-serie-text">
                    <b>{serie.trainSerieName || serie.trainSerieNumber}</b>
                    {serie.trainSerieOperator && <span>{serie.trainSerieOperator}</span>}
                    {serie.trainSerieManufacturer && <span>{serie.trainSerieManufacturer}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="kbs-drillin-section">
            <h5>Fahrt</h5>
            {/* No "Ziel" row here — start and destination already stand in the header block */}
            <ul className="train-facts">
              {train.kbs_number && (
                <li>
                  <span>Kursbuchstrecke</span>
                  <b>
                    <button className="train-kbs-link" onClick={() => onSelectKbs(train.kbs_number)}>
                      KBS {train.kbs_number} <i className="fa fa-chevron-right"></i>
                    </button>
                  </b>
                </li>
              )}
              {train.tracking?.last_seen && (
                <li>
                  <span>Zuletzt abgerufen</span>
                  <b title={`${train.tracking.poll_count} Abfragen, erstmals gesehen `
                    + formatClock(train.tracking.first_seen)}>
                    {formatClock(train.tracking.last_seen)}
                    {/* The API measured this against the moment we asked for, so it is
                        right in both modes; recomputing it here would not be. */}
                    <em className="tp-fact-age">{describeMinutes(train.tracking.age_minutes)}</em>
                  </b>
                </li>
              )}
              <li>
                <span>Position</span>
                <b>{train.position_source === 'actual'
                  ? 'gemeldet'
                  : train.position_source === 'last_known'
                    ? 'zuletzt gesehen'
                    : 'aus Fahrplan geschätzt'}</b>
              </li>
              {opDay && <li><span>Betriebstag</span><b>{opDay}</b></li>}
            </ul>
          </div>
        </>
      )}

      {activeTab === 'history' && (
        <HistoryBlock
          history={history}
          loading={historyState === 'loading'}
          failed={historyState === 'failed'}
        />
      )}

      {activeTab === 'route' && (
        <div className="kbs-drillin-section">
          <div className="tp-route">
            {stations.map((stop, i) => {
              const arrDelay = getDelay(stop.arrivalTime_scheduled, stop.arrivalTime_actual);
              const depDelay = getDelay(stop.departureTime_scheduled, stop.departureTime_actual);
              // The stop's colour follows its arrival delay — that is what a passenger waits for
              const stopDelay = arrDelay ?? depDelay ?? 0;
              const stopColor = cancelled ? '#8b949e' : delayColor(stopDelay);
              const passed = i <= lastPassedIdx;
              const isPrev = i === lastPassedIdx;
              const isNext = i === lastPassedIdx + 1 && lastPassedIdx >= 0;
              return (
                <React.Fragment key={`${stop.stationName}-${i}`}>
                <div className={`tp-stop ${passed ? 'passed' : ''} ${isPrev || isNext ? 'current' : ''}`}>
                  <span className="tp-stop-rail">
                    {i > 0 && <span className={`tp-rail-line up ${passed ? 'done' : ''}`} />}
                    <span
                      className="tp-stop-dot"
                      style={{ borderColor: stopColor, background: passed ? stopColor : 'var(--bg-card)' }}
                    />
                    {i < stations.length - 1 && (
                      <span className={`tp-rail-line down ${i < lastPassedIdx ? 'done' : ''}`} />
                    )}
                  </span>
                  <div className="tp-stop-body">
                    <div className="tp-stop-name">
                      {stop.stationName}
                      {stop.track_scheduled && (
                        <span className="tp-stop-track">Gl. {stop.track_actual || stop.track_scheduled}</span>
                      )}
                    </div>
                    <div className="tp-stop-times">
                      {stop.arrivalTime_scheduled && (
                        <span>an {clock(stop.arrivalTime_scheduled)}
                          {arrDelay ? <b className="tp-late"> +{arrDelay}</b> : null}
                        </span>
                      )}
                      {stop.departureTime_scheduled && (
                        <span>ab {clock(stop.departureTime_scheduled)}
                          {depDelay ? <b className="tp-late"> +{depDelay}</b> : null}
                        </span>
                      )}
                    </div>
                  </div>
                  {(!!stop.capacityLevelClass1 || !!stop.capacityLevelClass2) && (
                    <span className="tp-stop-capacity">
                      {!!stop.capacityLevelClass1 && (
                        <span className={`capacity-dot capacity-${stop.capacityLevelClass1}`}
                          title={`1. Klasse: Stufe ${stop.capacityLevelClass1}`} />
                      )}
                      {!!stop.capacityLevelClass2 && (
                        <span className={`capacity-dot capacity-${stop.capacityLevelClass2}`}
                          title={`2. Klasse: Stufe ${stop.capacityLevelClass2}`} />
                      )}
                    </span>
                  )}
                </div>

                {/* Where the train stands right now, on the section it is running */}
                {isPrev && progress !== null && (
                  <div className="tp-here">
                    <span className="tp-here-rail">
                      <span className="tp-rail-line up done" />
                      <span className="tp-here-marker" style={{ background: color }}>
                        <i className="fa fa-train"></i>
                      </span>
                      <span className="tp-rail-line down" />
                    </span>
                    <span className="tp-here-text">
                      unterwegs · {Math.round(progress * 100)} % des Abschnitts
                      {train.current_delay > 0 && (
                        <b style={{ color: delayColor(train.current_delay) }}>
                          {' '}+{train.current_delay} min
                        </b>
                      )}
                    </span>
                  </div>
                )}
                </React.Fragment>
              );
            })}
          </div>
          {hasCapacity && (
            <div className="capacity-legend">
              <span className="capacity-legend-title">Auslastung:</span>
              <span className="capacity-legend-item"><span className="capacity-dot capacity-1"></span> Gering</span>
              <span className="capacity-legend-item"><span className="capacity-dot capacity-2"></span> Mittel</span>
              <span className="capacity-legend-item"><span className="capacity-dot capacity-3"></span> Hoch</span>
              <span className="capacity-legend-item"><span className="capacity-dot capacity-4"></span> Sehr hoch</span>
            </div>
          )}
        </div>
      )}

      {activeTab === 'attributes' && (
        <div className="kbs-drillin-section">
          <div className="tp-attributes">
            {attributes.map((attr, i) => {
              const mapped = attributeLabels[attr.categorieKey];
              return (
                <span key={attr.UUID || i} className="tp-attribute">
                  <i className={`fa ${mapped?.icon || 'fa-info-circle'}`}></i>
                  {mapped?.label || attr.categorieValue || attr.categorieKey}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="kbs-drillin-section">
          {messages.map((msg, i) => (
            <div key={msg.UUID || i} className={`tp-message ${msg.priority >= 2 ? 'high' : ''}`}>
              {msg.messageText}
            </div>
          ))}
        </div>
      )}

      {showFullDetailLink && (
        <button className="train-panel-detail" onClick={() => onOpenDetail(train)}>
          Vollansicht öffnen <i className="fa fa-chevron-right"></i>
        </button>
      )}
    </aside>
  );
};

export default TrainPanel;
